/**
 * SimpleEntryForm - Dead-simple "I spent money" / "I received money" form
 * Creates proper double-entry journal entries without any accounting knowledge.
 *
 * "I Spent Money"    -> Debit expense account, Credit asset account
 * "I Received Money" -> Debit asset account,   Credit revenue account
 */

import React, { useState, useEffect } from 'react';
import { Account, JournalEntryCreateRequest } from '../../types/accounting';
import { accountingApi } from '../../services/accountingApi';
import EntryAttachments from './EntryAttachments';

interface SimpleEntryFormProps {
  businessId: number;
  onEntryCreated?: () => void;
}

type TransactionMode = 'spent' | 'received' | null;

const SimpleEntryForm: React.FC<SimpleEntryFormProps> = ({ businessId, onEntryCreated }) => {
  const [mode, setMode] = useState<TransactionMode>(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [categoryAccountId, setCategoryAccountId] = useState<number | null>(null);
  const [paymentAccountId, setPaymentAccountId] = useState<number | null>(null);

  const [accounts, setAccounts] = useState<Record<string, Account[]>>({});
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedEntryId, setSavedEntryId] = useState<number | null>(null);

  useEffect(() => {
    loadAccounts();
  }, [businessId]);

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await accountingApi.getBusinessAccounts(businessId);
      if (response.status === 'success' && response.data?.accounts) {
        setAccounts(response.data.accounts);
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setError('Could not load accounts. Please try again.');
    } finally {
      setAccountsLoading(false);
    }
  };

  const expenseAccounts = accounts['expense'] || [];
  const revenueAccounts = accounts['revenue'] || [];
  const assetAccounts = (accounts['asset'] || []).filter(
    (a) => a.is_active && (
      a.account_name.toLowerCase().includes('cash') ||
      a.account_name.toLowerCase().includes('bank') ||
      a.account_name.toLowerCase().includes('chequing') ||
      a.account_name.toLowerCase().includes('checking') ||
      a.account_name.toLowerCase().includes('savings')
    )
  );
  // Fallback: if no cash/bank accounts matched the filter, show all active asset accounts
  const paymentAccounts = assetAccounts.length > 0
    ? assetAccounts
    : (accounts['asset'] || []).filter((a) => a.is_active);

  const categoryAccounts = mode === 'spent' ? expenseAccounts : revenueAccounts;

  const resetForm = () => {
    setAmount('');
    setDescription('');
    setEntryDate(new Date().toISOString().split('T')[0]);
    setCategoryAccountId(null);
    setPaymentAccountId(null);
    setError(null);
  };

  const selectMode = (newMode: TransactionMode) => {
    setMode(newMode);
    resetForm();
    setSuccessMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid amount greater than zero.');
      return;
    }
    if (!description.trim()) {
      setError('Please enter a description.');
      return;
    }
    if (!categoryAccountId) {
      setError('Please select a category.');
      return;
    }
    if (!paymentAccountId) {
      setError('Please select a payment method.');
      return;
    }

    // Build double-entry journal entry
    let lines: JournalEntryCreateRequest['lines'];

    if (mode === 'spent') {
      // Debit expense, Credit asset (payment method)
      lines = [
        { account_id: categoryAccountId, description: description.trim(), debit_amount: parsedAmount, credit_amount: 0 },
        { account_id: paymentAccountId, description: description.trim(), debit_amount: 0, credit_amount: parsedAmount },
      ];
    } else {
      // Debit asset (payment method), Credit revenue
      lines = [
        { account_id: paymentAccountId, description: description.trim(), debit_amount: parsedAmount, credit_amount: 0 },
        { account_id: categoryAccountId, description: description.trim(), debit_amount: 0, credit_amount: parsedAmount },
      ];
    }

    const entryData: JournalEntryCreateRequest = {
      entry_date: entryDate,
      description: description.trim(),
      entry_type: 'general',
      lines,
    };

    setSubmitting(true);
    setSavedEntryId(null);
    try {
      const createResponse = await accountingApi.createJournalEntry(businessId, entryData);
      if (createResponse.status === 'success' && createResponse.data?.entry) {
        // Auto-post the entry so it takes effect immediately
        try {
          await accountingApi.postJournalEntry(createResponse.data.entry.id);
        } catch {
          // Entry created but not posted - still a success, just draft
        }

        const verb = mode === 'spent' ? 'Expense' : 'Income';
        setSuccessMessage(`${verb} of $${parsedAmount.toFixed(2)} recorded successfully!`);
        setSavedEntryId(createResponse.data.entry.id); // so an invoice or receipt can be attached to it
        resetForm();
        if (onEntryCreated) {
          onEntryCreated();
        }
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to create entry. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Styles ──────────────────────────────────────────────

  const containerStyle: React.CSSProperties = {
    maxWidth: 520,
    margin: '0 auto',
    padding: 24,
  };

  const buttonRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 24,
  };

  const modeButtonBase: React.CSSProperties = {
    flex: '1 1 200px',
    padding: '20px 16px',
    fontSize: 18,
    fontWeight: 600,
    border: '2px solid transparent',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'center',
  };

  const spentButtonStyle: React.CSSProperties = {
    ...modeButtonBase,
    backgroundColor: mode === 'spent' ? '#dc2626' : '#fff',
    color: mode === 'spent' ? '#fff' : '#dc2626',
    border: `2px solid #dc2626`,
  };

  const receivedButtonStyle: React.CSSProperties = {
    ...modeButtonBase,
    backgroundColor: mode === 'received' ? '#059669' : '#fff',
    color: mode === 'received' ? '#fff' : '#059669',
    border: `2px solid #059669`,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 600,
    marginBottom: 6,
    fontSize: 14,
    color: '#333',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    fontSize: 16,
    border: '1px solid #ccc',
    borderRadius: 6,
    boxSizing: 'border-box',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    appearance: 'auto' as any,
  };

  const fieldGroupStyle: React.CSSProperties = {
    marginBottom: 18,
  };

  const submitColor = mode === 'spent' ? '#dc2626' : '#059669';

  const submitButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    fontSize: 17,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: submitting ? '#999' : submitColor,
    border: 'none',
    borderRadius: 8,
    cursor: submitting ? 'not-allowed' : 'pointer',
    marginTop: 8,
  };

  const successStyle: React.CSSProperties = {
    padding: '14px 16px',
    backgroundColor: '#ecfdf5',
    border: '1px solid #059669',
    borderRadius: 8,
    color: '#059669',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: 18,
  };

  // errorStyle removed - using shared .error-message class from shared.css

  // ── Render ──────────────────────────────────────────────

  if (accountsLoading) {
    return <div style={containerStyle}><p>Loading accounts...</p></div>;
  }

  return (
    <div style={containerStyle}>
      {/* Mode selector */}
      <div style={buttonRowStyle}>
        <button
          type="button"
          style={spentButtonStyle}
          onClick={() => selectMode('spent')}
        >
          I Spent Money
        </button>
        <button
          type="button"
          style={receivedButtonStyle}
          onClick={() => selectMode('received')}
        >
          I Received Money
        </button>
      </div>

      {/* Success banner, with somewhere to put the paperwork */}
      {successMessage && (
        <div style={successStyle}>{successMessage}</div>
      )}
      {savedEntryId && (
        <div style={{ margin: '0 0 16px', padding: '12px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <EntryAttachments businessId={businessId} entryId={savedEntryId} />
        </div>
      )}

      {/* Form (only visible when a mode is selected) */}
      {mode && (
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          {/* Amount */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Amount ($)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={inputStyle}
              required
              autoFocus
            />
          </div>

          {/* Description */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Description</label>
            <input
              type="text"
              placeholder={mode === 'spent' ? 'e.g. Office supplies from Staples' : 'e.g. Payment from client ABC'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={inputStyle}
              required
            />
          </div>

          {/* Date */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Date</label>
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              style={inputStyle}
              required
            />
          </div>

          {/* Category */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              {mode === 'spent' ? 'Expense Category' : 'Revenue Category'}
            </label>
            <select
              value={categoryAccountId ?? ''}
              onChange={(e) => setCategoryAccountId(e.target.value ? Number(e.target.value) : null)}
              style={selectStyle}
              required
            >
              <option value="">-- Select category --</option>
              {categoryAccounts
                .filter((a) => a.is_active)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.account_name}
                  </option>
                ))}
            </select>
            {categoryAccounts.length === 0 && (
              <p style={{ color: '#999', fontSize: 13, marginTop: 4 }}>
                No {mode === 'spent' ? 'expense' : 'revenue'} accounts found. Please create accounts in Chart of Accounts first.
              </p>
            )}
          </div>

          {/* Payment method */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              {mode === 'spent' ? 'Paid From' : 'Received Into'}
            </label>
            <select
              value={paymentAccountId ?? ''}
              onChange={(e) => setPaymentAccountId(e.target.value ? Number(e.target.value) : null)}
              style={selectStyle}
              required
            >
              <option value="">-- Select account --</option>
              {paymentAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.account_name}
                </option>
              ))}
            </select>
            {paymentAccounts.length === 0 && (
              <p style={{ color: '#999', fontSize: 13, marginTop: 4 }}>
                No bank/cash accounts found. Please create accounts in Chart of Accounts first.
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            style={submitButtonStyle}
            disabled={submitting}
          >
            {submitting
              ? 'Saving...'
              : mode === 'spent'
                ? 'Record Expense'
                : 'Record Income'}
          </button>
        </form>
      )}

      {/* Prompt when no mode selected yet */}
      {!mode && !successMessage && (
        <p style={{ textAlign: 'center', color: '#888', marginTop: 12 }}>
          Choose an option above to get started.
        </p>
      )}
    </div>
  );
};

export default SimpleEntryForm;
